import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { AlertOctagon, AlertTriangle, Info } from 'lucide-react';
import { db } from '../../lib/firebase';
import { useAuth } from '../../lib/auth';
import { DEMO_SQUAWKS } from '../../lib/demoData';
import type { SquawkDoc, SquawkSeverity } from '../../lib/types';
import { SQUAWK_SEV_LABEL } from '../../lib/squawks';
import { PageHeader } from '../../components/PageHeader';
import { FilterChip } from '../../components/FilterChip';
import { Spinner } from '../../components/Spinner';
import { TRACKED_REG } from '../../lib/flightTrack';

const SEV_ICON: Record<SquawkSeverity, React.ReactNode> = {
  informational: <Info size={16} className="text-sky-600" />,
  should_fix_soon: <AlertTriangle size={16} className="text-amber-600" />,
  grounding: <AlertOctagon size={16} className="text-red-600" />,
};

// Remedied squawks (resolved / not-an-issue) live on the Resolved tab.
const RESOLVED = new Set(['resolved', 'cannot_reproduce', 'not_an_issue']);
function isResolved(s: SquawkDoc) {
  return RESOLVED.has(s.status);
}

type SquawkTab = 'active' | 'resolved';

// resolvedAt is only set once an owner closes the squawk, and older records
// predate the field — fall back to when it was reported.
function millis(s: SquawkDoc) {
  const t = s.resolvedAt ?? s.createdAt;
  return t?.toDate ? t.toDate().getTime() : Date.parse(s.dateDiscovered) || 0;
}

export function Squawks() {
  const { fbUser, demoMode } = useAuth();
  const [squawks, setSquawks] = useState<SquawkDoc[] | null>(null);
  // Pilots land on what still affects the airplane today.
  const [tab, setTab] = useState<SquawkTab>('active');

  useEffect(() => {
    if (demoMode) {
      setSquawks(DEMO_SQUAWKS);
      return;
    }
    if (!db) return;
    const unsub = onSnapshot(
      query(collection(db, 'squawks'), orderBy('createdAt', 'desc')),
      (snap) => setSquawks(snap.docs.map((d) => ({ ...(d.data() as SquawkDoc), squawkId: d.id })))
    );
    return () => unsub();
  }, [demoMode]);

  const all = squawks ?? [];
  const openCount = all.filter((s) => !isResolved(s)).length;
  const resolvedCount = all.length - openCount;

  // Active: newest reported first. Resolved: most recently closed first.
  const shown = squawks
    ? all.filter((s) => (tab === 'resolved' ? isResolved(s) : !isResolved(s))).sort((a, b) => millis(b) - millis(a))
    : null;

  return (
    <>
      <PageHeader
        title="Aircraft squawks"
        subtitle={`Every squawk on ${TRACKED_REG}, active and remedied, so you always know the aircraft’s status.`}
        actions={
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <Link to="/squawks/new" className="btn-primary text-sm">
              Report a squawk
            </Link>
            <FilterChip active={tab === 'active'} onClick={() => setTab('active')}>
              Active{openCount > 0 ? ` · ${openCount}` : ''}
            </FilterChip>
            <FilterChip active={tab === 'resolved'} onClick={() => setTab('resolved')}>
              Resolved{resolvedCount > 0 ? ` · ${resolvedCount}` : ''}
            </FilterChip>
          </div>
        }
      />

      {!shown ? (
        <Spinner label="Loading" />
      ) : shown.length === 0 ? (
        <div className="card p-8 text-center text-ink-muted">
          {tab === 'resolved'
            ? 'Nothing remedied yet.'
            : all.length === 0
            ? 'No squawks on record. If you find something on a pre-flight, report it here.'
            : 'No open squawks — the airplane is clean.'}
        </div>
      ) : (
        <div className="space-y-3">
          {shown.map((s) => {
            const resolved = isResolved(s);
            const mine = fbUser && s.reportedBy === fbUser.uid;
            return (
              <div key={s.squawkId} className="card p-4 flex items-start gap-3">
                <div className="mt-0.5">{SEV_ICON[s.severity]}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-medium text-navy-900 text-sm">{SQUAWK_SEV_LABEL[s.severity]}</div>
                    <span
                      className={
                        resolved
                          ? 'badge-success'
                          : s.status === 'in_progress'
                          ? 'badge-warning'
                          : s.status === 'acknowledged'
                          ? 'badge-info'
                          : 'badge-neutral'
                      }
                    >
                      {s.status === 'resolved' ? 'remedied' : s.status.replace('_', ' ')}
                    </span>
                  </div>
                  <div className="text-sm mt-1 text-ink">{s.description}</div>
                  {s.adminNotes && (
                    <div className="mt-1.5 rounded-lg bg-slate-50 px-2.5 py-1.5 text-xs text-ink-muted">
                      <span className="font-medium text-navy-800">Owner / maintenance:</span> {s.adminNotes}
                    </div>
                  )}
                  <div className="text-xs text-ink-muted mt-1.5">
                    Reported {s.dateDiscovered}
                    {s.reportedByName ? ` · by ${s.reportedByName}` : ''}
                    {mine ? (s.reportedByName ? ' (you)' : ' · by you') : ''}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
